import { EnumUserLogType, EventType, UserLogType, UserType } from "globals/types/models.ts";
import TYPES from "config/store/types.ts";
import { Dispatch } from "redux";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import { addOrReplaceArr, deleteArr, deleteSimpleArr } from "utils/transformer.ts";
import { getUserIsCompleted } from "utils/data/user.ts";
import { PaymentMethod } from "@stripe/stripe-js";
import { EnumEventStatTypeType, EnumEventStatValueType, InvoiceType, SubscriptionType } from "globals/types/globals.ts";
import { STORAGE_JWT_KEY } from "utils/data/global.ts";

// Define a type for the slice state
interface State {
  user: UserType | null
  userIsCompleted: boolean
  jwt: string | null,
  isCheck: boolean,

  paymentMethods: PaymentMethod[]
  invoices: InvoiceType[]
  subscription: SubscriptionType | null
  myEvents: EventType[]
  favoriteEventIds: number[]
  //totalEventCountCreated: number
}

const localJwt = window.localStorage.getItem(STORAGE_JWT_KEY) || null;
ajax.defaults.headers.common["Authorization"] = localJwt ? `Bearer ${localJwt}` : "";

// Define the initial state using that type
const initialState: State = {
  user: null,
  userIsCompleted: false,
  jwt: localJwt,
  isCheck: false,

  paymentMethods: [],
  invoices: [],
  subscription: null,

  myEvents: [],
  favoriteEventIds: []
  //totalEventCountCreated: 0
};

export const authReducer = (state = initialState, action: { payload: any, type: keyof typeof TYPES }): State => {
  const myEvents = state.myEvents;
  const favoriteEventIds = state.favoriteEventIds;

  switch (action.type) {
    case TYPES.SET_JWT:
      const jwt = action.payload as string | null;
      ajax.defaults.headers.common["Authorization"] = jwt ? `Bearer ${jwt}` : "";
      if (jwt) {
        window.localStorage.setItem(STORAGE_JWT_KEY, jwt);
      } else {
        window.localStorage.removeItem(STORAGE_JWT_KEY);
      }

      return {
        ...state,
        jwt: jwt
      };
    case TYPES.SET_USER:
      return {
        ...state,
        userIsCompleted: getUserIsCompleted(action.payload),
        user: action.payload
      };
    case TYPES.SET_AUTH_IS_CHECK:
      return {
        ...state,
        isCheck: action.payload
      };
    case TYPES.GET_MY_EVENTS:
      return {
        ...state,
        myEvents: action.payload
      };
    case TYPES.GET_MY_FAVORITE_EVENTS:
      return {
        ...state,
        myEvents: action.payload,//all data
        favoriteEventIds: action.payload.map(e => e.id)//just ids
      };
    case TYPES.MAKE_EVENT_FAVORITE:
      return {
        ...state,
        favoriteEventIds: addOrReplaceArr([...favoriteEventIds], action.payload)
      };

    case TYPES.REMOVE_EVENT_FAVORITE:
      return {
        ...state,
        favoriteEventIds: deleteSimpleArr([...favoriteEventIds], action.payload)
      };
    /* case TYPES.GET_MY_EVENT_COUNT_CREATED:
         return {
             ...state,
             totalEventCountCreated: action.payload
         } */
    case TYPES.CREATE_EVENT:
      return {
        ...state,
        myEvents: addOrReplaceArr([...myEvents], action.payload)
      };
    case TYPES.UPDATE_EVENT:
      return {
        ...state,
        myEvents: addOrReplaceArr([...myEvents], action.payload)
      };
    case TYPES.DELETE_EVENT:
      return {
        ...state,
        myEvents: deleteArr([...myEvents], { id: action.payload })
      };

    case TYPES.GET_MY_PAYMENT_METHODS:
      return {
        ...state,
        paymentMethods: action.payload
      };
    case TYPES.GET_MY_INVOICES:
      return {
        ...state,
        invoices: action.payload
      };
    case TYPES.GET_MY_SUBSCRIPTION:
      return {
        ...state,
        subscription: action.payload
      };
    default:
      return state;
  }
};


export const authActions = {
  //@ts-ignore
  sendUserLog: (type: EnumUserLogType, content: UserLogType["content"]) => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.post(`/public/auth/user-logs`, {
      type,
      ...content
    }));

    if (err) {
      throw err;
    }
    return res.data;
  },
  setJwt: (jwt: string | null) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.SET_JWT,
      payload: jwt
    });
  },
  initAuthCheck: () => async (dispatch: Dispatch, getState: any) => {
    const state: State = getState().auth;

    if (!state.jwt) {
      dispatch({
        type: TYPES.SET_AUTH_IS_CHECK,
        payload: true
      });
      return;
    }

    const [res, err] = await tryAsyncFunc(ajax.get("/connected/me"));

    if (err) {
      dispatch({
        type: TYPES.SET_JWT,
        payload: null
      });

      dispatch({
        type: TYPES.SET_AUTH_IS_CHECK,
        payload: true
      });
      throw err;
    }

    //set user before auth check
    dispatch({
      type: TYPES.SET_USER,
      payload: res.data.user
    });
    dispatch({
      type: TYPES.SET_AUTH_IS_CHECK,
      payload: true
    });

  },
  setUser: (user: UserType | null) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.SET_USER,
      payload: user
    });
  },
  checkIfEventIsFavorite: (eventId: number) => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get(`/connected/events/id/${eventId}/favorite`));

    if (err) {
      throw err;
    }

    //just update the state
    if (res.data.isFavorite) {
      dispatch({
        type: TYPES.MAKE_EVENT_FAVORITE,
        payload: eventId
      });
    }
  },
  getMyFavoriteEvents: () => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get("/connected/events/favorite"));

    if (err) {
      throw err;
    }

    dispatch({
      type: TYPES.GET_MY_FAVORITE_EVENTS,
      payload: res.data.events
    });
    return res.data;
  },
  getMyEvents: () => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get("/connected/events"));

    if (err) {
      throw err;
    }

    dispatch({
      type: TYPES.GET_MY_EVENTS,
      payload: res.data.events
    });
    /*  dispatch({
          type: TYPES.GET_MY_EVENT_COUNT_CREATED,
          payload: res.data.totalEventCountCreated
      }) */
    return res.data;
  },
  getMyPaymentMethods: () => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get(`/connected/me/payment-methods`));

    if (err) {
      throw err;
    }

    dispatch({
      type: TYPES.GET_MY_PAYMENT_METHODS,
      payload: res.data.paymentMethods
    });
    return res.data.paymentMethods;
  },
  getMyInvoices: () => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get(`/connected/me/invoices`));

    if (err) {
      throw err;
    }

    dispatch({
      type: TYPES.GET_MY_INVOICES,
      payload: res.data.invoices
    });
    return res.data.invoices;
  },
  getMySubscription: () => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get(`/connected/me/subscription`));

    if (err) {
      throw err;
    }

    dispatch({
      type: TYPES.GET_MY_SUBSCRIPTION,
      payload: res.data.subscription
    });
    return res.data.subscription;
  },
  getMyEventByUuid: (uuid: string) => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get(`/connected/events/uuid/${uuid}`));

    if (err) {
      throw err;
    }

    dispatch({
      type: TYPES.CREATE_EVENT,
      payload: res.data.event
    });
    return res.data.event;
  },
  makeEventFavorite: (eventId: number) => async (dispatch: Dispatch) => {
    //so ui dont have to wait for backend
    dispatch({
      type: TYPES.MAKE_EVENT_FAVORITE,
      payload: eventId
    });

    const [res, err] = await tryAsyncFunc(ajax.post(`/connected/events/id/${eventId}/favorite`));

    if (err) {
      throw err;
    }
  },
  removeEventFavorite: (eventId: number) => async (dispatch: Dispatch) => {
    //so ui dont have to wait for backend
    dispatch({
      type: TYPES.REMOVE_EVENT_FAVORITE,
      payload: eventId
    });

    const [res, err] = await tryAsyncFunc(ajax.delete(`/connected/events/id/${eventId}/favorite`));
    if (err) {
      throw err;
    }
  },
  createdEvent: (event: EventType) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.CREATE_EVENT,
      payload: event
    });
  },
  updatedEvent: (event: EventType) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.UPDATE_EVENT,
      payload: event
    });
  },
  deletedEvent: (id: number) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.DELETE_EVENT,
      payload: id
    });
  },

  //@ts-ignore
  statCollectionEventByUuid: (uuid: string, type: EnumEventStatTypeType, value: EnumEventStatValueType) => async (dispatch: Dispatch) => {
    const [_, err] = await tryAsyncFunc(ajax.post(`/public/events/${uuid}/stats`, {
      type: type,
      value: value
    }));

    if (err) {
      throw err;
    }
  }

};
